import {
    FaBuilding,
    FaCalendarAlt,
    FaJava,
    FaPython,
    FaHtml5,
    FaCss3Alt,
    FaReact,
    FaDatabase,
    FaDocker,
    FaLinux,
    FaAws,
    FaAngular,
    FaSuitcase,
    FaProjectDiagram,
} from "react-icons/fa";
import {
    SiSpringboot,
    SiPostgresql,
    SiOracle,
    SiJavascript,
    SiApachekafka,
    SiRabbitmq,
    SiNestjs,
    SiApachenifi,
    SiPrimeng,
    SiSpring,
    SiAmazondynamodb,
    SiAmazonec2,
    SiAmazons3,
    SiAmazonsqs,
} from "react-icons/si";
import { JSX } from "react";

// Define the shape of an experience item for better type safety
interface ExperienceItem {
    company: string;
    role: string;
    period: string;
    description: string;
    technologies: string[];
}

const experiences: ExperienceItem[] = [
    {
        company: "OSP International CALA",
        role: "Analista I+D II",
        period: "mar 2026 - Actualidad",
        description:
            "Desarrollo de microservicios para la integración con un motor BPM propietario. Creación de soluciones a medida para orquestar y optimizar flujos de trabajo en entidades del sector público y privado.",
        technologies: ["Java", "JavaServerFaces", "PrimeFaces", "CSS", "PostgreSQL", "Angular", "Spring Boot", "Python", "BPM"],
    },
    {
        company: "Universidad de Pamplona",
        role: "Analista de Desarrollo",
        period: "mar 2025 - feb 2026",
        description:
            "Desarrollo, optimización y mantenimiento de plataformas de software académico y administrativo. Creación de soluciones tecnológicas escalables para apoyar la operatividad de múltiples instituciones de educación superior.",
        technologies: ["Java", "HTML5", "CSS", "JavaScript", "PostgreSQL", "Oracle"],
    },
    {
        company: "Sofka Technologies",
        role: "Consultor de Desarrollo",
        period: "feb 2023 - ene 2024",
        description:
            "Diseño y desarrollo de servicios REST y arquitecturas de microservicios basadas en principios de arquitectura hexagonal para los sectores fintech y retail. Implementación de flujos reactivos y mensajería asíncrona.",
        technologies: [
            "Java",
            "Spring Boot",
            "WebFlux",
            "NestJS",
            "Kafka",
            "RabbitMQ",
            "Apache Nifi",
            "Docker",
            "AWS",
            "PostgreSQL",
            "MongoDb",
            "DynamoDb",
            "EC2",
            "S3",
            "SQS",
        ],
    },
    {
        company: "Universidad de Pamplona",
        role: "Analista de Bases de Datos (DBA)",
        period: "ago 2021 - ene 2023",
        description:
            "Administración y mantenimiento de bases de datos relacionales. Optimización avanzada de consultas SQL y gestión de servidores basados en Linux para asegurar un alto rendimiento y disponibilidad del sistema.",
        technologies: ["Oracle", "PostgreSQL", "SQL", "Linux"],
    },
    {
        company: "BeGo",
        role: "Desarrollador de Software",
        period: "ago 2019 - jul 2021",
        description:
            "Desarrollo de software integral para la gestión automatizada de pedidos, control de inventarios y ventas, enfocado en mejorar la eficiencia operativa de restaurantes y almacenes.",
        technologies: ["Java", "Python", "HTML5", "CSS", "React", "PostgreSQL", "Oracle"],
    },
];

// Helper function to map technologies to their specific tailwind color classes
const getTechColor = (tech: string): string => {
    const colors: Record<string, string> = {
        Java: "bg-orange-400 text-orange-800",
        Python: "bg-yellow-100 text-yellow-800",
        HTML5: "bg-red-100 text-red-800",
        CSS: "bg-blue-100 text-blue-800",
        JavaScript: "bg-yellow-200 text-yellow-900",
        React: "bg-cyan-100 text-cyan-800",
        Spring: "bg-green-100 text-green-800",
        "Spring Boot": "bg-green-100 text-green-800",
        WebFlux: "bg-green-100 text-green-500",
        PostgreSQL: "bg-indigo-100 text-indigo-800",
        Oracle: "bg-purple-100 text-purple-800",
        SQL: "bg-teal-100 text-teal-800",
        Linux: "bg-gray-200 text-gray-800",
        Kafka: "bg-fuchsia-100 text-fuchsia-800",
        RabbitMQ: "bg-orange-200 text-orange-900",
        AWS: "bg-amber-100 text-amber-800",
        Docker: "bg-blue-200 text-blue-900",
        NestJS: "bg-pink-100 text-pink-800",
        "Apache Nifi": "bg-rose-100 text-purple-800",
        "Bases de datos": "bg-emerald-100 text-emerald-800",
        Angular: "bg-cyan-100 text-red-600",
        PrimeFaces: "bg-gray-200 text-blue-800",
        JavaServerFaces: "bg-red-100 text-amber-800",
        MongoDb: "bg-green-100 text-green-800", // MongoDB's brand color is leaf green
        DynamoDb: "bg-blue-100 text-blue-800",  // AWS Database services are typically blue
        EC2: "bg-orange-100 text-orange-800",   // AWS Compute services use orange
        S3: "bg-red-100 text-red-800",          // AWS Storage services are usually red
        SQS: "bg-pink-100 text-pink-800",       // AWS App Integration services use pink/magenta
        BPM: "bg-indigo-100 text-indigo-800", // Un tono índigo o violeta le va muy bien
    };

    return colors[tech] || "bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100";
};

const getTechIcon = (tech: string): JSX.Element | null => {
    const icons: Record<string, JSX.Element> = {
        Java: <FaJava />,
        Python: <FaPython />,
        HTML5: <FaHtml5 />,
        CSS: <FaCss3Alt />,
        JavaScript: <SiJavascript />,
        React: <FaReact />,
        "Spring Boot": <SiSpringboot />,
        WebFlux: <SiSpring />,
        PostgreSQL: <SiPostgresql />,
        Oracle: <SiOracle />,
        SQL: <FaDatabase />,
        Linux: <FaLinux />,
        Kafka: <SiApachekafka />,
        RabbitMQ: <SiRabbitmq />,
        AWS: <FaAws />,
        Docker: <FaDocker />,
        NestJS: <SiNestjs />,
        "Apache Nifi": <SiApachenifi />,
        "Bases de datos": <FaDatabase />,
        Angular: <FaAngular />,
        PrimeFaces: <SiPrimeng />,
        JavaServerFaces: <FaJava />,
        MongoDb: <FaDatabase />,
        DynamoDb: <SiAmazondynamodb />,
        EC2: <SiAmazonec2 />,
        S3: <SiAmazons3 />,
        SQS: <SiAmazonsqs />,
        BPM: <FaProjectDiagram />,
    };

    return icons[tech] || null;
};

const Experience = () => {
    return (
        <section className="px-4 py-12 bg-gradient-to-b from-gray-600 text-white pt-40">
            <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold mb-10 text-center text-gray-800 dark:text-white">
                    Experiencia Profesional
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {experiences.map((exp, index) => (
                        <div
                            key={index}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex flex-col gap-4"
                        >
                            <div className="flex-1">
                                <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                                    <FaBuilding className="text-gray-400 dark:text-gray-300" />
                                    {exp.company}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    <FaSuitcase className="text-gray-400" /> {exp.role}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-2">
                                    <FaCalendarAlt className="text-gray-400" /> {exp.period}
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 mb-2">{exp.description}</p>
                                
                                {/* Render the list of technologies as pill-shaped badges */}
                                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                                    {exp.technologies.map((tech, i) => (
                                        <span
                                            key={i}
                                            className={`text-sm font-medium px-3 py-1 rounded-full inline-flex items-center gap-1 ${getTechColor(tech)}`}
                                        >
                                            {getTechIcon(tech)}
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Experience;
